import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Dropdown,
  DropdownProps,
  Option,
  OptionOnSelectData,
  SelectionEvents,
  Toast,
  ToastTitle,
  makeStyles,
  mergeClasses,
  useToastController,
} from "@fluentui/react-components";
import { useErrorBoundary } from "react-error-boundary";
import useFrameTemplates from "../../services/templates/useFrameTemplate";
import { FrameTemplate } from "../../interfaces";

interface FrameTemplateDropdownProps extends Partial<DropdownProps> {
  onFrameChange?: (frame: FrameTemplate | null) => void;
  selectedFrameId?: string;
}

const useStyles = makeStyles({
  optionCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 14px",
    borderRadius: "12px",
    border: "1px solid #e1e3e6",
    backgroundColor: "#fff",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    width: "100%",
    boxSizing: "border-box",
  },
  optionCardSelected: {
    backgroundColor: "#f5f5f5",
    border: "1px solid #d5d7db",
    boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
  },
  frameImageWrapper: {
    width: "44px",
    height: "44px",
    borderRadius: "6px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  frameImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  frameName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

const FrameTemplateDropdown: React.FC<FrameTemplateDropdownProps> = (props) => {
  const {
    onFrameChange,
    onOptionSelect,
    selectedFrameId: selectedFrameIdProp,
    value: controlledValue,
    selectedOptions: controlledSelectedOptions,
    disabled: controlledDisabled,
    ...dropdownProps
  } = props;

  const styles = useStyles();
  const { dispatchToast } = useToastController();
  const { showBoundary } = useErrorBoundary();

  const [frameTemplates, setFrameTemplates] = useState<FrameTemplate[] | null>(null);
  const [internalSelectedFrameId, setInternalSelectedFrameId] = useState<string | undefined>();
  const isControlled = selectedFrameIdProp !== undefined;
  const shouldAutoSelectDefaultRef = useRef(selectedFrameIdProp === undefined);

  React.useEffect(() => {
    shouldAutoSelectDefaultRef.current = selectedFrameIdProp === undefined;
  }, [selectedFrameIdProp]);

  const loadFrameTemplates = useCallback(async () => {
    try {
      const { response } = await useFrameTemplates();
      const rawData = response?.data?.results ?? response?.data ?? [];
      const fetchedFrames: FrameTemplate[] = Array.isArray(rawData) ? rawData : [];
      setFrameTemplates(fetchedFrames);

      if (shouldAutoSelectDefaultRef.current) {
        const defaultFrame = fetchedFrames.find((frame) => frame.is_default);
        if (defaultFrame) {
          setInternalSelectedFrameId(defaultFrame.frame_id);
          onFrameChange?.(defaultFrame);
        } else {
          setInternalSelectedFrameId(undefined);
          onFrameChange?.(null);
        }
      }
    } catch (responseError: any) {
      if (responseError?.code === "ERR_NETWORK") showBoundary(responseError);
      console.error(responseError);
      dispatchToast(
        <Toast>
          <ToastTitle>Something went wrong. Please reach out to us at support@qrmark.com</ToastTitle>
        </Toast>
      );
    }
  }, [dispatchToast, onFrameChange, showBoundary]);

  React.useEffect(() => {
    // noinspection JSIgnoredPromiseFromCall
    loadFrameTemplates();
  }, [loadFrameTemplates]);

  const effectiveSelectedFrameId = isControlled
    ? selectedFrameIdProp || undefined
    : internalSelectedFrameId;

  const selectedFrame = useMemo(() => {
    if (!frameTemplates || !effectiveSelectedFrameId) return undefined;
    return frameTemplates.find((frame) => frame.frame_id === effectiveSelectedFrameId);
  }, [frameTemplates, effectiveSelectedFrameId]);

  const selectedFrameName = controlledValue ?? selectedFrame?.name ?? "";

  const resolvedSelectedOptions =
    controlledSelectedOptions ?? (effectiveSelectedFrameId ? [effectiveSelectedFrameId] : []);
  const hasFrames = Boolean(frameTemplates && frameTemplates.length > 0);
  const isDropdownDisabled = controlledDisabled ?? !hasFrames;

  const handleFrameSelection = (event: SelectionEvents, data: OptionOnSelectData) => {
    if (!data.optionValue) {
      if (!isControlled) {
        setInternalSelectedFrameId(undefined);
      }
      onFrameChange?.(null);
    } else {
      const frame = frameTemplates?.find((template) => template.frame_id === data.optionValue) ?? null;
      if (!isControlled) {
        setInternalSelectedFrameId(frame?.frame_id);
      }
      if (frame) onFrameChange?.(frame);
    }

    onOptionSelect?.(event, data);
  };

  return (
    <Dropdown
      aria-labelledby="frame-template"
      placeholder="Select Frame Template"
      size="medium"
      clearable
      value={selectedFrameName}
      selectedOptions={resolvedSelectedOptions}
      onOptionSelect={handleFrameSelection}
      disabled={isDropdownDisabled}
      {...dropdownProps}
    >
      {frameTemplates &&
        frameTemplates.map((frame) => (
          <Option key={frame.frame_id} value={frame.frame_id} text={frame.name}>
            <div
              className={mergeClasses(
                styles.optionCard,
                effectiveSelectedFrameId === frame.frame_id && styles.optionCardSelected
              )}
            >
              <div className={styles.frameImageWrapper}>
                <img
                  src={frame.build_content?.file}
                  alt={frame.name}
                  className={styles.frameImage}
                  onError={(event) => {
                    event.currentTarget.style.visibility = "hidden";
                  }}
                />
              </div>
              <span className={styles.frameName}>{frame.name}</span>
            </div>
          </Option>
        ))}
    </Dropdown>
  );
};

export default FrameTemplateDropdown;
